import styles from './Rm.module.scss';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import InfiniteTable from 'components/infinite-table/InfiniteTable';
import AnimatedPieChart from 'components/animated-pie-chart/AnimatedPieChart';

import { detailedViewTable as s1Headers } from 'constants/scenarioOne';
import { detailedViewTable as s2Headers } from 'constants/scenarioTwo';
import { detailedViewTable as s3Headers } from 'constants/scenarioThree';
import { detailedViewTable as s4Headers } from 'constants/scenarioFour';

import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence } from 'framer-motion';
import {
    selectMarketValue,
    selectRmPeerComparisonData,
    selectRmTrendData,
    selectRmValue,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth,
    selectRmScoreDecompositionData,
    selectZoneValue,
    selectFiltersAllValues
} from 'selectors/selectors';
import { Scenarios } from 'types/types';
import { generateRequestParamsForRmApi } from 'utils/api';
import {
    rmPeerComparisonChartOptions,
    rmTrendAnalysisChartOptions
} from 'utils/charts';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import {
    fetchRmInvestigationData,
    fetchRmPageStats,
    fetchRmPeerComparisonData,
    fetchRmTrendData,
    fetchRmScoreDecomposition
} from 'slices/RmSlice';
import PieChart from 'components/pie-chart/PieChart';
import { SCENARIO_LIST } from 'constants/components';
import { useLocation } from 'react-router-dom';
import { isTypeNumber } from '../../utils/common';

const Rm = ({ navigateTo }) => {
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    const [scenarioList, setScenarioList] = useState();
    const [dwSelectedScenario, setDwSelectedScenario] = useState();

    const [isAnimatedChartVisible, setIsAnimatedChartVisible] = useState(true);

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const allValues = useSelector(selectFiltersAllValues);

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const trendData = useSelector(selectRmTrendData);
    const scoreDecompositionData = useSelector(selectRmScoreDecompositionData);
    const peerComparisonData = useSelector(selectRmPeerComparisonData);

    const generateScenarioListData = () => [
        {
            value: Scenarios.One,
            label: 'Scenario 1',
            headers: s1Headers,
            hasData: true
        },
        {
            value: Scenarios.Two,
            label: 'Scenario 2',
            headers: s2Headers,
            hasData: true
        },
        {
            value: Scenarios.Three,
            label: 'Scenario 3',
            headers: s3Headers,
            hasData: true
        },
        {
            value: Scenarios.Four,
            label: 'Scenario 4',
            headers: s4Headers,
            hasData: true
        }
    ];

    const onScenarioChanged = (event) => {
        const { value } = event.target;
        setDwSelectedScenario(scenarioList.find((s) => s.value === value));
    };

    useEffect(() => {
        const scenarioOptions = generateScenarioListData();

        setScenarioList(scenarioOptions);
        setDwSelectedScenario(
            scenarioOptions.filter((s) => s.hasData)[0] || scenarioOptions[0]
        );
    }, []);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                const checkedRmValue =
                    allValues.rm.value === rmValue ? null : rmValue;

                dispatch(
                    fetchRmTrendData(
                        generateRequestParamsForRmApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            null,
                            null,
                            checkedRmValue,
                            null,
                            tsEnd.epoch
                        )
                    )
                );

                dispatch(
                    fetchRmPeerComparisonData(
                        generateRequestParamsForRmApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            null,
                            null,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );

                dispatch(
                    fetchRmPageStats(
                        generateRequestParamsForRmApi(
                            null,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );

                dispatch(
                    fetchRmInvestigationData(
                        generateRequestParamsForRmApi(
                            null,
                            null,
                            null,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );

                if (checkedRmValue) {
                    dispatch(
                        fetchRmScoreDecomposition(
                            generateRequestParamsForRmApi(
                                isTypeNumber(zoneValue) ? zoneValue : null,
                                null,
                                null,
                                checkedRmValue,
                                tsStart.epoch,
                                tsEnd.epoch
                            )
                        )
                    );
                }
            }
        }
    }, [rmValue, tsStart, tsEnd]);

    useEffect(() => {
        if (rmValue) {
            toggleAnimatedPopup(true);
        }
    }, [rmValue]);

    const onClickPeerComparison = (param) => {
        const { name, seriesIndex } = param;

        SCENARIO_LIST.map((scenario) => {
            if (name === scenario.name && seriesIndex === 0)
                navigateTo(pathname, scenario.path);
        });
    };
    const onPeerComparisonEvents = {
        click: onClickPeerComparison
    };

    const toggleAnimatedPopup = (isVisible) => {
        setIsAnimatedChartVisible(isVisible);
    };

    const chartRef = useRef(null);
    useEffect(() => {
        chartRef.current.getEchartsInstance().dispose();
        chartRef.current
            .getEchartsInstance()
            .setOption(rmTrendAnalysisChartOptions(trendData));
    }, [trendData]);

    return (
        <div style={{ position: 'relative' }}>
            <AnimatePresence>
                {isAnimatedChartVisible &&
                    scoreDecompositionData &&
                    scoreDecompositionData.totalScore > 0 && (
                        <div>
                            <AnimatedPieChart
                                chartData={scoreDecompositionData}
                                setVisibility={toggleAnimatedPopup}
                            />
                        </div>
                    )}
            </AnimatePresence>

            <ContentContainer>
                <div className={styles.chartsContainer}>
                    <ChartWrapper
                        comboClass={styles.rightMargin}
                        style={{ position: 'relative' }}
                    >
                        <PieChart
                            chartData={scoreDecompositionData}
                            setVisibility={toggleAnimatedPopup}
                        />
                    </ChartWrapper>

                    <ChartWrapper comboClass={styles.rightMargin}>
                        <ReactECharts
                            option={rmTrendAnalysisChartOptions(trendData)}
                            style={{ width: '100%', height: '100%' }}
                            ref={chartRef}
                        ></ReactECharts>
                    </ChartWrapper>

                    <ChartWrapper>
                        <ReactECharts
                            option={rmPeerComparisonChartOptions(
                                peerComparisonData
                            )}
                            style={{ width: '100%', height: '100%' }}
                            onEvents={onPeerComparisonEvents}
                        ></ReactECharts>
                    </ChartWrapper>
                </div>
                <div className={styles.tableContainer}>
                    <div className={styles.tableHeader}>
                        <div className={styles.title}>Detailed View</div>
                        {dwSelectedScenario && (
                            <div className={styles.scenarioSelection}>
                                <select
                                    onChange={(e) => onScenarioChanged(e)}
                                    value={dwSelectedScenario.value}
                                >
                                    {scenarioList.map((s) => (
                                        <option
                                            key={s.value}
                                            value={s.value}
                                            label={s.label}
                                        />
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    {dwSelectedScenario && dwSelectedScenario.hasData && (
                        <InfiniteTable
                            zone={zoneValue}
                            market={marketValue}
                            team={teamValue}
                            rm={rmValue}
                            start={tsStart}
                            end={tsEnd}
                            allValues={allValues}
                            columns={dwSelectedScenario.headers}
                            scenario={dwSelectedScenario.value}
                            topCss={{ top: '-45px' }}
                        />
                    )}
                    {dwSelectedScenario && !dwSelectedScenario.hasData && (
                        <div className={styles.noDataNotice}>
                            No results found for selected scenario
                        </div>
                    )}
                </div>
            </ContentContainer>
        </div>
    );
};

Rm.propTypes = {
    navigateTo: PropTypes.func
};
export default Rm;
